import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Loader2 } from "lucide-react";
import type { SiiEmisor } from "../types";

const emisorFormSchema = z.object({
  rut: z.string().min(1, "RUT es requerido"),
  razonSocial: z.string().min(1, "Razón Social es requerida"),
  giro: z.string().min(1, "Giro es requerido"),
  codigoActividad: z.number().min(1, "Código de actividad es requerido"),
  direccion: z.string().min(1, "Dirección es requerida"),
  comuna: z.string().min(1, "Comuna es requerida"),
  ciudad: z.string().min(1, "Ciudad es requerida"),
  resolutionNumber: z.number().min(0, "Número de resolución es requerido"),
  resolutionDate: z.string().min(1, "Fecha de resolución es requerida"),
  rutRepresentante: z.string().optional(),
});

type EmisorFormValues = z.infer<typeof emisorFormSchema>;

interface SiiConfigFormProps {
  emisor: SiiEmisor | null;
  onSave: (emisor: SiiEmisor) => Promise<void>;
  isLoading?: boolean;
}

/**
 * Form for SII company information (Emisor context).
 */
export function SiiConfigForm({ emisor, onSave, isLoading }: SiiConfigFormProps) {
  const form = useForm<EmisorFormValues>({
    resolver: zodResolver(emisorFormSchema),
    defaultValues: {
      rut: emisor?.rut ?? "",
      razonSocial: emisor?.razonSocial ?? "",
      giro: emisor?.giro ?? "",
      codigoActividad: emisor?.codigoActividad ?? 0,
      direccion: emisor?.direccion ?? "",
      comuna: emisor?.comuna ?? "",
      ciudad: emisor?.ciudad ?? "",
      resolutionNumber: emisor?.resolutionNumber ?? 0,
      resolutionDate: emisor?.resolutionDate ?? "",
      rutRepresentante: emisor?.rutRepresentante ?? "",
    },
  });

  // Reset form when emisor data changes (e.g., after API fetch)
  useEffect(() => {
    if (emisor) {
      form.reset({
        rut: emisor.rut ?? "",
        razonSocial: emisor.razonSocial ?? "",
        giro: emisor.giro ?? "",
        codigoActividad: emisor.codigoActividad ?? 0,
        direccion: emisor.direccion ?? "",
        comuna: emisor.comuna ?? "",
        ciudad: emisor.ciudad ?? "",
        resolutionNumber: emisor.resolutionNumber ?? 0,
        resolutionDate: emisor.resolutionDate ?? "",
        rutRepresentante: emisor.rutRepresentante ?? "",
      });
    }
  }, [emisor, form]);

  const handleSubmit = async (values: EmisorFormValues) => {
    await onSave(values);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* RUT */}
          <FormField
            control={form.control}
            name="rut"
            render={({ field }) => (
              <FormItem>
                <FormLabel>RUT Empresa</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="77.938.104-8" />
                </FormControl>
                <FormDescription>
                  RUT con puntos y guión
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Razón Social */}
          <FormField
            control={form.control}
            name="razonSocial"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Razón Social</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="MI EMPRESA SPA" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Giro */}
          <FormField
            control={form.control}
            name="giro"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Giro</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="DESARROLLO DE SOFTWARE" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Código Actividad Económica */}
          <FormField
            control={form.control}
            name="codigoActividad"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Código Actividad Económica</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    placeholder="620200"
                    value={field.value || ""}
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : 0)}
                    onBlur={field.onBlur}
                    name={field.name}
                    ref={field.ref}
                  />
                </FormControl>
                <FormDescription>
                  Código SII de actividad económica
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Dirección */}
          <FormField
            control={form.control}
            name="direccion"
            render={({ field }) => (
              <FormItem className="md:col-span-2">
                <FormLabel>Dirección</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Av. Providencia 1234, Of. 567" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Comuna */}
          <FormField
            control={form.control}
            name="comuna"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Comuna</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Providencia" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Ciudad */}
          <FormField
            control={form.control}
            name="ciudad"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Ciudad</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Santiago" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="border-t pt-6">
          <h3 className="text-sm font-medium mb-4">Representante Legal / Certificado</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* RUT Representante */}
            <FormField
              control={form.control}
              name="rutRepresentante"
              render={({ field }) => (
                <FormItem className="md:col-span-2">
                  <FormLabel>RUT del Titular del Certificado</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="12.345.678-9" />
                  </FormControl>
                  <FormDescription>
                    RUT de la persona dueña del certificado digital (PFX).
                    Debe estar autorizado en SII para enviar documentos en nombre de la empresa.
                    Dejar vacío si el certificado está a nombre de la empresa.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="border-t pt-6">
          <h3 className="text-sm font-medium mb-4">Resolución SII</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Número Resolución */}
            <FormField
              control={form.control}
              name="resolutionNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nº Resolución</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="0"
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : 0)}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                    />
                  </FormControl>
                  <FormDescription>
                    Número de la resolución que autoriza la emisión
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Fecha Resolución */}
            <FormField
              control={form.control}
              name="resolutionDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fecha Resolución</FormLabel>
                  <FormControl>
                    <Input {...field} type="date" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={form.formState.isSubmitting || isLoading}
          >
            {form.formState.isSubmitting || isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Guardando...
              </>
            ) : (
              "Guardar"
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}
